import m from 'mithril';
import { Store } from '../store';
import { t } from '../i18n';
import { Button } from '../components/Buttons';
import { ProductCard } from '../components/ProductCard';

export const HomePage: m.Component = {
    oninit() {
        this.heroTimer = setInterval(() => {
            if (Store.state.promotions?.length > 1) {
                this.heroIndex = (this.heroIndex + 1) % Store.state.promotions.length;
                m.redraw();
            }
        }, 5000);
    },
    onremove() {
        if (this.heroTimer) clearInterval(this.heroTimer);
    },
    heroIndex: 0,
    heroTimer: null as any,
    view(vnode: any) {
        const ctrl = vnode.state;
        const { products, loading, error, activeCategory, sortBy, promotions } = Store.state;

        // Filter
        let filtered = activeCategory === 'all'
            ? products
            : products.filter((p: any) =>
                (p.category ?? '').toLowerCase().includes(activeCategory.toLowerCase()));
        // Sort
        if (sortBy === 'price-asc')  filtered = [...filtered].sort((a: any, b: any) => Number(a.price) - Number(b.price));
        if (sortBy === 'price-desc') filtered = [...filtered].sort((a: any, b: any) => Number(b.price) - Number(a.price));
        if (sortBy === 'name')       filtered = [...filtered].sort((a: any, b: any) => a.name.localeCompare(b.name));

        // Recently Viewed
        let recentlyViewedIds: string[] = [];
        try {
            recentlyViewedIds = JSON.parse(localStorage.getItem('recently_viewed') || '[]');
        } catch (e) {}
        
        const recentlyViewedProducts = recentlyViewedIds
            .map(id => products.find(p => String(p.id) === id))
            .filter(Boolean);

        return m('div', { class: 'text-[var(--color-text-base)]' }, [

            /* ── HERO ──────────────────────────────────────────────────── */
            activeCategory === 'all' ? ((promotions && promotions.length > 0) ? m('section', { class: 'relative overflow-hidden rounded-lg mb-4 min-h-[340px] sm:min-h-[420px] flex items-center shadow-lg transition-all duration-700' }, [
                m('div', { 
                    class: 'absolute inset-0 bg-cover bg-center transition-all duration-1000 scale-105', 
                    style: `background-image: url(${promotions[ctrl.heroIndex].banner_url}); filter: brightness(0.6);` 
                }),
                m('div', { class: 'absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent' }),
                m('div', { class: 'relative z-10 flex flex-col sm:flex-row items-center justify-between w-full px-8 sm:px-12 py-10 gap-8' }, [
                    m('div', { class: 'flex flex-col gap-5 text-white max-w-lg animate-fade-in-up' }, [
                        promotions[ctrl.heroIndex].discount_percent && m('div', { class: 'inline-flex items-center gap-2 bg-[var(--color-primary)] text-white px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest shadow-lg' }, [
                            m('iconify-icon', { icon: 'lucide:tag', class: 'text-sm' }),
                            `${promotions[ctrl.heroIndex].discount_percent}% OFF`
                        ]),
                        m('h1', { class: 'text-3xl sm:text-4xl xl:text-5xl font-black leading-tight tracking-tight drop-shadow-lg' }, promotions[ctrl.heroIndex].title),
                        m('p', { class: 'text-white/80 text-sm sm:text-base leading-relaxed font-medium max-w-md drop-shadow-md' }, promotions[ctrl.heroIndex].description),
                        m('div', { class: 'flex flex-wrap gap-3 mt-2' }, [
                            m('button', {
                                class: 'bg-[var(--color-primary)] text-white font-black text-sm px-6 py-3 rounded-xl hover:bg-[var(--color-primary-dark)] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center gap-2 cursor-pointer',
                                onclick: () => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }),
                            }, [
                                'Shop Offer', m('iconify-icon', { icon: 'lucide:arrow-right' })
                            ])
                        ])
                    ])
                ]),
                // Dots
                promotions.length > 1 && m('div', { class: 'absolute bottom-6 left-0 right-0 flex justify-center gap-2 z-20' }, 
                    promotions.map((_, i) => m('button', { 
                        class: `w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${i === ctrl.heroIndex ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60'}`,
                        onclick: () => { ctrl.heroIndex = i; m.redraw(); }
                    }))
                )
            ]) : m('section', { class: 'relative overflow-hidden rounded-lg mb-4 min-h-[340px] sm:min-h-[420px] flex items-center' }, [
                // Gradient background
                m('div', { class: 'absolute inset-0 bg-gradient-to-br from-[var(--color-primary)] via-[var(--color-secondary)] to-[var(--color-primary)] opacity-90 pointer-events-none' }),
                m('div', { class: 'absolute inset-0 pointer-events-none', style: 'background: radial-gradient(ellipse at 80% 50%, rgba(255,255,255,0.15) 0%, transparent 60%)' }),

                // Content
                m('div', { class: 'relative z-10 flex flex-col sm:flex-row items-center justify-between w-full px-8 sm:px-12 py-10 gap-8' }, [
                    // Left text
                    m('div', { class: 'flex flex-col gap-5 text-white max-w-lg' }, [
                        m('div', { class: 'inline-flex items-center gap-2 bg-white/20 border border-white/30 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest backdrop-blur-sm' }, [
                            m('iconify-icon', { icon: 'lucide:sparkles', class: 'text-sm animate-pulse' }),
                            t('hero.subtitle') || 'New Collection 2026',
                        ]),
                        m('h1', { class: 'text-3xl sm:text-4xl xl:text-5xl font-black leading-tight tracking-tight' }, [
                            t('hero.title') || 'Health & Joy,',
                            m('br'),
                            m('span', { class: 'text-white/80' }, 'Delivered to You'),
                        ]),
                        m('p', { class: 'text-white/75 text-sm sm:text-base leading-relaxed font-medium max-w-md' },
                            t('hero.description')
                        ),
                        m('div', { class: 'flex flex-wrap gap-3' }, [
                            m('button', {
                                class: 'bg-white text-[var(--color-primary)] font-black text-sm px-6 py-3 rounded-lg hover:bg-white/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center gap-2',
                                onclick: () => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }),
                            }, [
                                t('hero.cta') || 'Shop Now',
                                m('iconify-icon', { icon: 'lucide:arrow-right' }),
                            ]),
                            m('button', {
                                class: 'border-2 border-white/40 text-white font-bold text-sm px-6 py-3 rounded-lg hover:bg-white/10 transition-all backdrop-blur-sm',
                            }, 'View Lookbook'),
                        ]),
                    ]),

                    // Right: floating stats card (shows on sm+)
                    m('div', { class: 'hidden sm:flex flex-col gap-3' }, [
                        m('div', { class: 'bg-white/15 backdrop-blur-md border border-white/25 rounded-lg p-6 text-white min-w-[200px]' }, [
                            m('p', { class: 'text-xs uppercase tracking-widest opacity-70 mb-4 font-bold' }, 'Store Stats'),
                            ...[
                                ['9k+', 'Happy Customers', 'lucide:users'],
                                ['4.9★', 'Average Rating', 'lucide:star'],
                                ['30d', 'Free Returns', 'lucide:refresh-ccw'],
                            ].map(([val, label, icon]) =>
                                m('div', { class: 'flex items-center gap-3 mb-3 last:mb-0' }, [
                                    m('iconify-icon', { icon, class: 'text-white/60 text-lg shrink-0' }),
                                    m('div', [
                                        m('div', { class: 'font-black text-lg leading-none' }, val),
                                        m('div', { class: 'text-[10px] opacity-60 font-medium' }, label),
                                    ]),
                                ])
                            ),
                        ]),
                    ]),
                ]),
            ])) : null,

            /* ── FEATURES STRIP ─────────────────────────────────────────── */
            activeCategory === 'all' ? m('div', { class: 'grid grid-cols-3 gap-3 mb-6' }, [
                ...([
                    { icon: 'lucide:truck',        label: 'Free Shipping',    desc: 'On all orders' },
                    { icon: 'lucide:shield-check', label: 'Secure Checkout',  desc: 'Bank-grade SSL' },
                    { icon: 'lucide:refresh-ccw',  label: 'Easy Returns',     desc: '30-day policy' },
                ].map(f =>
                    m('div', { class: 'hover-spring glass border border-[var(--color-border)] rounded-lg px-4 py-3 flex items-center gap-3 hover:border-[var(--color-primary)] transition-colors group' }, [
                        m('iconify-icon', { icon: f.icon, class: 'text-xl text-[var(--color-primary)] shrink-0 group-hover:scale-110 transition-transform' }),
                        m('div', { class: 'hidden sm:block min-w-0' }, [
                            m('p', { class: 'font-bold text-xs sm:text-sm leading-tight' }, f.label),
                            m('p', { class: 'text-[10px] opacity-50 font-medium' }, f.desc),
                        ]),
                    ])
                )),
            ]) : null,

            /* ── FEATURED PRODUCTS ──────────────────────────────────────── */
            (products.some(p => p.is_featured) && activeCategory === 'all') && m('section', { class: 'mb-8 mt-10' }, [
                m('div', { class: 'flex items-center justify-between mb-6' }, [
                    m('h2', { class: 'text-xl sm:text-2xl font-black tracking-tight flex items-center gap-2' }, [
                        m('iconify-icon', { icon: 'lucide:star', class: 'text-yellow-500' }),
                        'Featured Products'
                    ])
                ]),
                m('div', { class: 'grid grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6' },
                    products.filter(p => p.is_featured).slice(0, 5).map(p => m(ProductCard, { key: `feat-${p.id}`, product: p }))
                )
            ]),



            /* ── PRODUCTS GRID ──────────────────────────────────────────── */
            m('section', { id: 'products' }, [
                // Header
                m('div', { class: 'flex flex-col sm:flex-row sm:items-end justify-between gap-2 mb-5' }, [
                    m('div', [
                        m('span', { class: 'text-[10px] font-black tracking-widest uppercase text-[var(--color-primary)] opacity-80' },
                            activeCategory === 'all' ? 'All Products' : activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)
                        ),
                        m('h2', { class: 'text-xl sm:text-2xl font-black tracking-tight mt-0.5' }, t('products.title')),
                    ]),
                    !loading && m('p', { class: 'text-xs opacity-50 font-medium self-end' }, `${filtered.length} items`),
                ]),

                // Loading Skeletons
                loading ? m('div', { class: 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4' }, 
                    Array.from({ length: 10 }).map((_, i) => 
                        m('div', { key: `skel-${i}`, class: 'glass border border-[var(--color-border)] opacity-60 rounded-lg overflow-hidden flex flex-col' }, [
                            m('div', { class: 'w-full aspect-square bg-[var(--color-border)] animate-pulse' }),
                            m('div', { class: 'p-3 flex flex-col flex-1 gap-2' }, [
                                m('div', { class: 'h-2 w-1/3 bg-[var(--color-border)] rounded animate-pulse' }),
                                m('div', { class: 'h-4 w-3/4 bg-[var(--color-border)] rounded animate-pulse' }),
                                m('div', { class: 'h-3 w-full bg-[var(--color-border)] rounded animate-pulse mt-1' }),
                                m('div', { class: 'h-3 w-2/3 bg-[var(--color-border)] rounded animate-pulse' }),
                                m('div', { class: 'flex items-center justify-between pt-2 border-t border-[var(--color-border)] mt-2' }, [
                                    m('div', { class: 'h-5 w-1/4 bg-[var(--color-border)] rounded animate-pulse' }),
                                    m('div', { class: 'h-8 w-8 bg-[var(--color-border)] rounded-lg animate-pulse' }),
                                ]),
                            ])
                        ])
                    )
                )

                // Error
                : error ? m('div', { class: 'py-12 flex flex-col items-center gap-3 text-red-500 glass rounded-lg p-6 border border-red-500/20' }, [
                    m('iconify-icon', { icon: 'lucide:alert-triangle', class: 'text-4xl' }),
                    m('span', { class: 'font-bold text-sm text-center' }, error),
                    m('button', {
                        class: 'text-xs underline opacity-70 hover:opacity-100 mt-1',
                        onclick: () => { Store.initApp(); m.redraw(); },
                    }, 'Try again'),
                ])

                // Empty
                : filtered.length === 0 ? m('div', { class: 'py-16 flex flex-col items-center gap-3 opacity-50' }, [
                    m('iconify-icon', { icon: 'lucide:package-open', class: 'text-5xl' }),
                    m('span', { class: 'font-semibold text-sm' },
                        activeCategory === 'all' ? t('products.empty') : `No "${activeCategory}" products yet.`),
                ])

                // Product grid
                : m('div', { class: 'grid grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6' },
                    filtered.map((p: any) => m(ProductCard, { key: p.id, product: p }))
                ),
            ]),

            /* ── RECENTLY VIEWED ────────────────────────────────────────── */
            recentlyViewedProducts.length > 0 && m('section', { class: 'mt-16 pt-12 border-t border-[var(--color-border)]' }, [
                m('div', { class: 'flex items-center justify-between mb-6' }, [
                    m('h2', { class: 'text-xl sm:text-2xl font-black tracking-tight' }, 'Recently Viewed'),
                ]),
                m('div', { class: 'grid grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6 opacity-90 hover:opacity-100 transition-opacity' },
                    recentlyViewedProducts.map((p: any) => m(ProductCard, { key: `recent-${p.id}`, product: p }))
                )
            ]),
        ]);
    },
};
